/**
 * Data model for the editable Intelligence dashboards (KPIs + Forecast).
 *
 * A dashboard is a list of sections; a section is a titled 12-column grid of
 * widgets. Every widget stores its own grid footprint (`w` columns × `h`
 * rows) plus a kind-specific config that points at entries in the source
 * catalog (`catalog.ts`) by id — never at raw data. That keeps the stored
 * JSON small, lets the catalog evolve without migrating saved layouts, and
 * means a retired source degrades to an "unavailable" card rather than a
 * crash (see `parseLayout` in `layout.ts`).
 */

export const DASHBOARD_LAYOUT_VERSION = 1;

/** Grid columns per section row. Widths are expressed in these units. */
export const GRID_COLUMNS = 12;
/** Height of one grid row in px. Widget `h` is a multiple of this. */
export const GRID_ROW_PX = 88;
/** Gap between grid cells in px — must match the Tailwind `gap-4` on the grid. */
export const GRID_GAP_PX = 16;

export const MIN_WIDGET_W = 2;
export const MIN_WIDGET_H = 1;
export const MAX_WIDGET_H = 10;

export type DashboardPageKey = 'kpis' | 'forecast';

export type Unit =
  | 'currency'
  | 'count'
  | 'percent'
  | 'ratio'
  | 'days'
  | 'months'
  | 'seconds'
  | 'score'
  | 'multiple';

/** Which direction is "good" for a metric. Drives delta colouring. */
export type MetricDirection = 'up' | 'down' | 'neutral';

export type TimeGrain = 'day' | 'week' | 'month';

export type ChartType =
  | 'line'
  | 'area'
  | 'bar'
  | 'stacked'
  | 'combo'
  | 'donut'
  | 'hbar';

export type ForecastMethod = 'auto' | 'linear' | 'holt' | 'seasonal' | 'moving_avg';

export interface WidgetBase {
  id: string;
  /** Columns spanned (1..12). */
  w: number;
  /** Rows spanned (1..MAX_WIDGET_H). */
  h: number;
  /** Optional override of the catalog label. */
  title?: string;
}

export interface MetricWidget extends WidgetBase {
  kind: 'metric';
  metric: string;
  showSparkline: boolean;
  showDelta: boolean;
  /** Optional target in the metric's natural unit (cents, 0..1 ratio, count); the editor converts from display units. */
  target?: number | null;
}

export interface ChartSeriesConfig {
  metric: string;
  /** Hex colour; falls back to the palette by index. */
  color?: string;
  /** Per-series render override for `combo` charts. */
  kind?: 'bar' | 'line' | 'area';
  axis?: 'left' | 'right';
}

export interface ChartWidget extends WidgetBase {
  kind: 'chart';
  chartType: ChartType;
  /** Time-series charts: one entry per plotted metric. */
  series: ChartSeriesConfig[];
  /** Donut / hbar charts: id of a breakdown source. */
  breakdown?: string;
  grain: 'auto' | TimeGrain;
  /** Overlay the prior period as a dashed line. */
  compare: boolean;
  cumulative: boolean;
  showLegend: boolean;
  /** Breakdown charts: max slices before the rest fold into "Other". */
  limit?: number;
}

export interface ForecastWidget extends WidgetBase {
  kind: 'forecast';
  source: string;
  method: ForecastMethod;
  /** Periods ahead in the source's grain. 0 = inherit the page horizon. */
  horizon: number;
  /** Trailing history periods to display. 0 = inherit the page setting. */
  history: number;
  showBand: boolean;
  /** Overlay the manual projection (budget) series when the source has one. */
  showManual: boolean;
  /** Per-item sources: the catalog item id. */
  itemId?: string | null;
}

export interface TableWidget extends WidgetBase {
  kind: 'table';
  table: string;
  /** Column keys to show, in order. Empty = all columns. */
  columns: string[];
  limit: number;
  sort?: { key: string; dir: 'asc' | 'desc' } | null;
}

export interface TextWidget extends WidgetBase {
  kind: 'text';
  body: string;
}

export type DashboardWidget =
  | MetricWidget
  | ChartWidget
  | ForecastWidget
  | TableWidget
  | TextWidget;

export type WidgetKind = DashboardWidget['kind'];

export interface DashboardSection {
  id: string;
  title: string;
  description?: string;
  collapsed?: boolean;
  widgets: DashboardWidget[];
}

export interface DashboardLayout {
  version: typeof DASHBOARD_LAYOUT_VERSION;
  sections: DashboardSection[];
}

/** One point of a time series. `t` is `YYYY-MM-DD` (day) or `YYYY-MM-01` (month). */
export interface SeriesPoint {
  t: string;
  v: number;
}

/** A forecast point carries its confidence band. */
export interface ForecastPoint extends SeriesPoint {
  lo: number;
  hi: number;
}

export const WIDGET_KIND_LABELS: Record<WidgetKind, string> = {
  metric: 'Metric',
  chart: 'Chart',
  forecast: 'Forecast',
  table: 'Table',
  text: 'Note',
};

export const CHART_TYPE_LABELS: Record<ChartType, string> = {
  line: 'Line',
  area: 'Area',
  bar: 'Bars',
  stacked: 'Stacked bars',
  combo: 'Bars + line',
  donut: 'Donut',
  hbar: 'Ranked bars',
};

export const FORECAST_METHOD_LABELS: Record<ForecastMethod, string> = {
  auto: 'Auto (best fit)',
  linear: 'Linear trend',
  holt: 'Exponential smoothing (Holt)',
  seasonal: 'Seasonal (Holt-Winters)',
  moving_avg: 'Moving average',
};

export function isBreakdownChart(type: ChartType): boolean {
  return type === 'donut' || type === 'hbar';
}
